import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { distinctUntilChanged, take } from 'rxjs';

import { PuSubscribable } from '@app/utils/pu-subscribe';
import { GetInspectionRemindListConst } from '@main-application/inspections/models/inspection-notify-list.const';
import { EColorPalette } from '@shared/enums/color-palette.enum';
import { EIcon } from '@shared/enums/icon.enum';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { isExist } from '@shared/utils/exist.util';
import { IconComponent } from '@ui-components/components/icon/icon.component';
import { RadioButtonDropdownModule } from '@ui-components/controls/radio-button-dropdown/radio-button-dropdown.module';

import { CustomRemindDialogComponent } from '../custom-remind-dialog/custom-remind-dialog.component';
import {
  CustomNotifyId,
  DontNotifyId,
  DontNotifyOption,
  OnDueDateNotifyId,
} from '../inspection-add-regular-form/inspection-add-regular-form.component';

export const CompletedId = 30000;
export const UntilCompletedOption: IRadioButtonOption<number> = {
  value: CompletedId,
  label: `Completed`,
  sort: -CompletedId,
};

export type CustomNotificationFieldType = 'notifyOn' | 'remindOn' | 'thenRemindOn' | 'until';

@Component({
  selector: 'app-custom-notifications-dialog',
  templateUrl: './custom-notifications-dialog.component.html',
  styleUrls: ['./custom-notifications-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    RadioButtonDropdownModule,
    MatButtonModule,
    MatDialogModule,
    IconComponent,
    FormsModule,
    ReactiveFormsModule,
  ],
})
export class CustomNotificationsDialogComponent extends PuSubscribable implements OnInit {
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private formBuilder: FormBuilder = inject(FormBuilder);
  private dialogRef: MatDialogRef<CustomNotificationsDialogComponent> = inject(MatDialogRef);
  private dialog: MatDialog = inject(MatDialog);

  public data: {
    showCreateField?: boolean;
    dueDate: Date;
    notifyAssignee?: boolean;
    notifyOn?: number;
    remindOn?: number;
    thenRemindOn?: number;
    until?: number;
    notifyOptions?: IRadioButtonOption<number>[][];
    notifyLabel?: string;
  } = inject(MAT_DIALOG_DATA);

  form = this.formBuilder.group({
    notifyAssignee: [this.data.notifyAssignee],
    notifyOn: [OnDueDateNotifyId],
    remindOn: [DontNotifyId],
    thenRemindOn: [DontNotifyId],
    until: [CompletedId],
  });

  notifyOptions = this.data?.notifyOptions?.length
    ? this.addCustomNotifyOption(this.data.notifyOptions)
    : this.createNotifyOptions('notifyOn', GetInspectionRemindListConst('remindOn'), !this.data.showCreateField);
  notifyAssigneeOptions: IRadioButtonOption<boolean>[] = [
    { value: false, label: 'Do not Notify Assignee' },
    { value: true, label: 'Notify Assignee' },
  ];
  remindOptions = this.createNotifyOptions(
    'remindOn',
    this.mapOptionsToLabel(GetInspectionRemindListConst('remindOn')),
    true
  );
  remindThenOptions = this.createNotifyOptions('thenRemindOn', GetInspectionRemindListConst('thenRemindOn'), true);
  untilOptions = this.createNotifyOptions('until', GetInspectionRemindListConst('until'), true);

  DontNotifyId = DontNotifyId;
  EIcon = EIcon;
  EColorPalette = EColorPalette;
  orderError = false;

  get thenRemindOn(): UntypedFormControl {
    return this.form.get('thenRemindOn') as UntypedFormControl;
  }

  get notifyAssignee(): UntypedFormControl {
    return this.form.get('notifyAssignee') as UntypedFormControl;
  }

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.initForm();
    this.initFormChanges();
  }

  close() {
    this.dialogRef.close();
  }

  save() {
    if (this.form.valid && !this.orderError) {
      this.dialogRef.close(this.form.value);
    }
  }

  private openCustomDateDialog(fieldType: CustomNotificationFieldType = 'notifyOn', prevValue?: number) {
    const canUsePrevValue =
      isExist(prevValue) && prevValue !== DontNotifyId && prevValue !== CustomNotifyId && prevValue !== -999;
    const existingValue = canUsePrevValue ? prevValue : null;
    const defaultNotifyValue = -14;
    let existingDays: number | undefined;
    let existingBeforeAfter: string | undefined;

    if (fieldType === 'notifyOn' || fieldType === 'remindOn') {
      const value = isExist(existingValue) ? existingValue : defaultNotifyValue;
      existingDays = Math.abs(value);
      existingBeforeAfter = value < 0 ? 'before' : 'after';
    } else if (fieldType === 'thenRemindOn' || fieldType === 'until') {
      if (isExist(existingValue)) {
        existingDays = existingValue as number;
      }
    }

    this.dialog
      .open(CustomRemindDialogComponent, {
        panelClass: 'modal-container-without-padding',
        width: '424px',
        autoFocus: false,
        data: {
          fieldType,
          existingBeforeAfter,
          existingDays,
          stepName: this.data?.notifyLabel,
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe(res => {
        if (res === undefined) {
          this.form.get(fieldType).setValue(prevValue ?? DontNotifyId);
          return;
        }

        const days = res.days;
        const options = this.getOptions(fieldType);
        const option = options.find(o => o.value === days);
        if (!option) {
          const newOption = this.createNewOption(fieldType, days);
          this.form.get(fieldType).setValue(newOption.value);
        } else {
          this.form.get(fieldType).setValue(option.value);
        }

        if (fieldType === 'notifyOn' || fieldType === 'remindOn') {
          this.checkNotifyOrder();
        }

        if (fieldType === 'remindOn' && this.form.value.notifyAssignee === false) {
          this.form.get('notifyAssignee').setValue(true);
        }
      })
      .untilDestroyed(this);
  }

  private createNewOption(fieldType: CustomNotificationFieldType, value: number): IRadioButtonOption<number> {
    const label = this.getOptionLabel(fieldType, value);
    const newOption = { value: value, label, sort: value };
    const options = this.getOptions(fieldType);
    options.push(newOption);
    this.updateOptions(fieldType, options);
    return newOption;
  }

  private getOptionLabel(fieldType: CustomNotificationFieldType, value: number): string {
    if (fieldType === 'until') {
      return value === 1 ? '1 time' : `${value} times`;
    }
    if (fieldType === 'thenRemindOn') {
      return `Every ${value}d`;
    }
    const baseLabel = this.data?.notifyLabel || 'due date';
    if (value === -999) {
      return 'As soon as NOTICE filed';
    }
    return value === 0
      ? `on ${baseLabel}`
      : value < 0
      ? `${Math.abs(value)}d before ${baseLabel}`
      : `${value}d after ${baseLabel}`;
  }

  private getOptions(fieldType: CustomNotificationFieldType): IRadioButtonOption<number>[] {
    switch (fieldType) {
      case 'notifyOn':
        return this.notifyOptions.flat().filter(option => option.value !== CustomNotifyId);
      case 'remindOn':
        return [...this.remindOptions[1]];
      case 'thenRemindOn':
        return [...this.remindThenOptions[1]];
      case 'until':
        return [...this.untilOptions[1]];
    }
    return [];
  }

  updateOptions(fieldType: CustomNotificationFieldType, newOptions: IRadioButtonOption<number>[]) {
    switch (fieldType) {
      case 'notifyOn':
        this.notifyOptions = this.createNotifyOptions(
          'notifyOn',
          newOptions.sort((a, b) => +a.sort - +b.sort)
        );
        break;
      case 'remindOn':
        this.remindOptions = this.createNotifyOptions(
          'remindOn',
          this.mapOptionsToLabel(newOptions).sort((a, b) => +a.sort - +b.sort)
        );
        break;
      case 'thenRemindOn':
        this.remindThenOptions = this.createNotifyOptions(
          'thenRemindOn',
          newOptions.sort((a, b) => +a.sort - +b.sort)
        );
        break;
      case 'until':
        this.untilOptions = this.createNotifyOptions(
          'until',
          newOptions.sort((a, b) => +a.sort - +b.sort)
        );
        break;
    }
  }

  private createNotifyOptions(
    fieldType: CustomNotificationFieldType,
    notifyOptions: IRadioButtonOption<number>[],
    addEmptyOption = false
  ): IRadioButtonOption<number>[][] {
    switch (fieldType) {
      case 'notifyOn':
        const customNotifyOn = this.getCustomOption('Set Custom Timing');
        if (addEmptyOption) {
          return [[customNotifyOn], [DontNotifyOption, ...notifyOptions]];
        }
        return [[customNotifyOn], notifyOptions];

      case 'remindOn':
        const customRemindOn = this.getCustomOption();
        if (addEmptyOption) {
          return [[customRemindOn], [{ ...DontNotifyOption, label: "Don't remind" }, ...notifyOptions]];
        }
        return [[customRemindOn], notifyOptions];

      case 'thenRemindOn':
        const customThenRemind = this.getCustomOption('Set custom frequency');
        if (addEmptyOption) {
          return [[customThenRemind], [{ ...DontNotifyOption, label: "Don't remind" }, ...notifyOptions]];
        }
        return [[customThenRemind], notifyOptions];

      case 'until':
        const customUntil = this.getCustomOption('Set custom');
        if (addEmptyOption) {
          return [[customUntil], [UntilCompletedOption, ...notifyOptions]];
        }
        return [[customUntil], notifyOptions];
    }
  }

  getCustomOption(label = 'Set Custom Timing') {
    return {
      value: CustomNotifyId,
      label,
      sort: 0,
    };
  }

  private addCustomNotifyOption(options: IRadioButtonOption<number>[][]): IRadioButtonOption<number>[][] {
    const customOption = this.getCustomOption('Set Custom Timing');
    return [[customOption], ...options];
  }

  private mapOptionsToLabel(options: IRadioButtonOption<number>[]): IRadioButtonOption<number>[] {
    const baseLabel = this.data?.notifyLabel;
    if (!baseLabel) {
      return options;
    }
    return options.map(option => ({
      ...option,
      label:
        option.value === -999
          ? 'As soon as NOTICE filed'
          : option.value === 0
          ? `on ${baseLabel}`
          : option.value < 0
          ? `${Math.abs(option.value)}d before ${baseLabel}`
          : `${option.value}d after ${baseLabel}`,
    }));
  }

  private initForm() {
    const { notifyOn, remindOn, thenRemindOn, until } = this.data;
    if (isExist(notifyOn) && !this.getOptions('notifyOn').find(o => o.value === notifyOn)) {
      this.createNewOption('notifyOn', notifyOn);
    }
    if (isExist(remindOn) && !this.remindOptions[1].find(o => o.value === remindOn)) {
      this.createNewOption('remindOn', remindOn);
    }
    if (thenRemindOn > 0 && !this.remindThenOptions[1].find(o => o.value === thenRemindOn)) {
      this.createNewOption('thenRemindOn', thenRemindOn);
    }
    if (until > 0 && !this.untilOptions[1].find(o => o.value === until)) {
      this.createNewOption('until', until);
    }
    this.form = this.formBuilder.group({
      notifyAssignee: [this.data.notifyAssignee],
      notifyOn: [notifyOn ?? OnDueDateNotifyId],
      remindOn: [remindOn ?? DontNotifyId],
      thenRemindOn: [thenRemindOn ?? DontNotifyId],
      until: [until ?? CompletedId],
    });
  }

  private initFormChanges() {
    let prevNotifyOnValue = this.form.controls.notifyOn.value;
    this.form.controls.notifyOn.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(notifyOn => {
        if (notifyOn === CustomNotifyId) {
          this.openCustomDateDialog('notifyOn', prevNotifyOnValue);
        }
        prevNotifyOnValue = notifyOn;

        if (notifyOn === DontNotifyId) {
          this.form.controls.notifyAssignee.setValue(false);
          this.form.controls.remindOn.setValue(DontNotifyId);
          this.form.controls.thenRemindOn.setValue(DontNotifyId);
          this.form.controls.until.setValue(CompletedId);
        }

        this.checkNotifyOrder();
      })
      .untilDestroyed(this);

    let prevRemindOnValue = this.form.controls.remindOn.value;
    this.form.controls.remindOn.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(remindOn => {
        if (remindOn === CustomNotifyId) {
          this.openCustomDateDialog('remindOn', prevRemindOnValue);
        }
        prevRemindOnValue = remindOn;

        if (remindOn !== DontNotifyId && remindOn !== CustomNotifyId && this.form.value.notifyOn === DontNotifyId) {
          this.form.controls.notifyOn.setValue(OnDueDateNotifyId);
        } else if (remindOn === DontNotifyId && this.form.value.thenRemindOn !== DontNotifyId) {
          this.form.controls.thenRemindOn.setValue(DontNotifyId);
        }

        if (remindOn !== DontNotifyId && this.form.value.notifyAssignee === false) {
          this.form.controls.notifyAssignee.setValue(true);
        }

        this.checkNotifyOrder();
      })
      .untilDestroyed(this);

    let prevThenRemindOnValue = this.form.controls.thenRemindOn.value;
    this.form.controls.thenRemindOn.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(thenRemindOn => {
        if (thenRemindOn === CustomNotifyId) {
          this.openCustomDateDialog('thenRemindOn', prevThenRemindOnValue);
        }
        prevThenRemindOnValue = thenRemindOn;

        if (
          thenRemindOn !== DontNotifyId &&
          thenRemindOn !== CustomNotifyId &&
          this.form.value.remindOn === DontNotifyId
        ) {
          const options = this.getOptions('remindOn');
          const option = options.find(o => o.value === thenRemindOn);
          if (!option) {
            const newOption = this.createNewOption('remindOn', thenRemindOn);
            this.form.get('remindOn').setValue(newOption.value);
          } else {
            this.form.get('remindOn').setValue(option.value);
          }
          this.form.controls.notifyAssignee.setValue(true);
        }

        if (thenRemindOn === DontNotifyId && this.form.value.until !== CompletedId) {
          this.form.controls.until.setValue(CompletedId);
        }
      })
      .untilDestroyed(this);

    let prevUntilValue = this.form.controls.until.value;
    this.form.controls.until.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(until => {
        if (until === CustomNotifyId) {
          this.openCustomDateDialog('until', prevUntilValue);
        }
        prevUntilValue = until;
      })
      .untilDestroyed(this);

    this.form.controls.notifyAssignee.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(notifyAssignee => {
        if (notifyAssignee === false) {
          this.form.controls.remindOn.setValue(DontNotifyId);
          this.form.controls.thenRemindOn.setValue(DontNotifyId);
          this.form.controls.until.setValue(CompletedId);
        }
      })
      .untilDestroyed(this);
  }

  private checkNotifyOrder() {
    setTimeout(() => {
      const { notifyOn, remindOn } = this.form.value;
      const notCustom = notifyOn !== CustomNotifyId && remindOn !== CustomNotifyId;
      const notEmpty = notifyOn !== DontNotifyId && remindOn !== DontNotifyId;
      this.orderError = notCustom && notEmpty && notifyOn >= remindOn;
      this.cdr.markForCheck();
    }, 100);
  }
}
